import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { IndexPage } from '../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../components/index-page/types';
import type { MenuItem } from '../../../components/Menu';
import { StatusText } from '../../../components/table/StatusText';
import { TextCell } from '../../../components/table/TextCell';
import { ApiError } from '../../../lib/apiError';
import { ApprovalRoutingModal } from '../components/ApprovalRoutingModal';
import { approvalRoutingApi } from '../approvalsApi';
import type { ApprovalCategoryListItem, ApprovalWorkflowOption } from '../approvalTypes';

/**
 * Which approvers sign off on each workflow.
 *
 * **There is no Clear data.** Emptying the routing table would silently hand every workflow in
 * the product to the administrator fallback, with nothing on any screen saying so — the call
 * `stock-movements` and `issuances` already take.
 */
export function ApprovalRoutingIndexPage() {
  const [editing, setEditing] = useState<ApprovalCategoryListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    row: ApprovalCategoryListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    THE PREREQUISITE, loaded separately: a workflow can only be configured if a module declares
    one, and on a product with no approval-bearing module the form would open with an empty
    required picker. Seeded MET and a failed load leaves it met — flashing "nothing to
    configure" on every load of a working page is worse than a banner arriving a moment late,
    and a request that did not come back is not evidence that there are no workflows.
  */
  const [workflows, setWorkflows] = useState<ApprovalWorkflowOption[]>([]);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    approvalRoutingApi
      .workflows(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) {
          setWorkflows(next);
          setWorkflowsLoaded(true);
        }
      })
      .catch(() => {
        // Fails soft: the banner stays met rather than claiming there is nothing to configure.
      });

    return () => controller.abort();
  }, [reloadKey]);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      approvalRoutingApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: ApprovalCategoryListItem): MenuItem[] => [
      {
        key: `edit-${row.id}`,
        label: 'Edit approvers',
        permission: 'approval-categories.create',
        onClick: () => setEditing(row),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'approval-categories.delete',
        disabledReason: row.isActive ? 'This routing is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await approvalRoutingApi.remove(confirming.row.id);
      else await approvalRoutingApi.restore(confirming.row.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<ApprovalCategoryListItem>
        module="approval-categories"
        moduleNoun="approval routing"
        breadcrumb={['Approvals', 'Routing']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['ApprovalCategory', 'ApprovalCategoryApprover']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        /*
          THE SEARCHABLE VALUE IS THE WORKFLOW KEY, said here rather than left to be discovered:
          the NAME is a live view resolved from the registered handler, so it is not a column
          and `LIKE` cannot reach it.
        */
        searchPlaceholder="Search by workflow key"
        emptyMessage="No workflow has approvers configured yet. Until one does, every submission routes to the administrators."
        onCreate={() => setCreating(true)}
        createPermission="approval-categories.create"
        /*
          NOT a `prerequisite`, and that is the shape of this page rather than a shortcut.

          A prerequisite names a CATALOG the reader can go and fill, and its `to` is required
          because the banner's whole job is to link there. A workflow is declared in CODE by
          whichever module submits through the engine — there is no page to send anybody to, so
          a prerequisite here would be a banner pointing nowhere.

          Disabled with the reason instead, which is what the reader can actually act on.
        */
        createDisabledReason={createDisabledReason(workflowsLoaded, workflows)}
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'deactivate', row })}
        deletePermission="approval-categories.delete"
        deleteLabel={() => 'Retire'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This routing is already retired.')}
        otherInfo={(row) => row}
      />

      {creating || editing !== null ? (
        <ApprovalRoutingModal
          open
          editingId={editing?.id ?? null}
          workflows={workflows}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Retire approval routing' : 'Restore routing'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`approval-routing-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.row.workflowName}" returns to the administrator fallback: the next submission routes to every active administrator, and any one of them completes it.`
                  : `"${confirming.row.workflowName}" routes to its configured approvers again.`}
              </p>

              {/*
                IT DOES LESS THAN ITS LABEL SUGGESTS, and the consequence goes before the tick.
                Requests already in flight carry their own snapshot of the chain and their own
                completion rule, so they are decided under what they were submitted under — a
                reader who expected those to change would otherwise see nothing happen.
              */}
              {confirming.kind === 'deactivate' ? (
                <p>
                  Requests already awaiting a decision keep the approvers they were submitted to.
                  This changes where the next submission routes, nothing more.
                </p>
              ) : null}

              <p>Nothing is erased — a retired routing can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * Why Create is unavailable right now.
 *
 * **Null while the workflows have not loaded**, which is the same reasoning a prerequisite
 * banner is seeded MET: a request that did not come back is not evidence that there are no
 * workflows, and disabling the page's primary action on a failed fetch would be worse than
 * letting the reader meet the server's own refusal.
 */
function createDisabledReason(
  loaded: boolean,
  workflows: ApprovalWorkflowOption[],
): string | null {
  if (!loaded) return null;

  if (workflows.length === 0) {
    return 'No module submits anything for approval yet, so there is no workflow to configure.';
  }

  if (workflows.every((w) => w.isConfigured)) {
    return 'Every workflow already has approvers. Edit one from its row instead.';
  }

  return null;
}

const COLUMNS: ColumnDef<ApprovalCategoryListItem>[] = [
  {
    // The workflow is what a reader calls the row — the routing has no name of its own.
    key: 'workflowKey',
    header: 'Workflow',
    width: 300,
    sortable: true,
    render: (row) => <TextCell value={row.workflowName} />,
  },
  {
    key: 'mode',
    header: 'Routing',
    width: 150,
    summary: true,
    render: (row) => <TextCell value={row.modeLabel} />,
  },
  {
    key: 'approverCount',
    header: 'Approvers',
    width: 120,
    align: 'right',
    /*
      NOT a summary value: the list view concatenates its summary values with no labels, so a
      count arrives as a bare `· 2` that a reader cannot interpret without this header.
    */
    render: (row) => <TextCell value={String(row.approverCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Retired</StatusText>
      ),
  },
];
